import { ReactNode, useEffect, useMemo } from 'react'
import {
  AtendimentoType,
  formatDateToPtBr,
  ModalidadeType,
  OptionsHelper,
  useDebounce,
  validateDateRange,
  VinculoType,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { RelatorioNpsFilters, useListFilters } from '../../core/ListFiltersProvider'
import { useAuth } from '../../../../../auth'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  DateInput,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

/** Filtros de select (IOption): o chip mostra o rótulo da opção e o × limpa o campo. */
type SelectFilterKey = Exclude<
  keyof RelatorioNpsFilters,
  'paciente' | 'protocolo' | 'range' | 'inicio' | 'fim'
>

/** Rótulo do campo de data no texto de filtros que vai impresso no PDF. */
const RANGE_PDF: Record<string, string> = {
  dataSolicitacao: 'Agendamento',
  dataInicio: 'Início',
  dataTermino: 'Finalizado',
}

type Props = {
  /** Conteúdo à direita (ex.: exportar PDF/CSV). */
  actions?: ReactNode
}

export const RelatorioNpsListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { isLoading } = useQueryResponse()
  const { currentUser } = useAuth()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()

  const optionsTiposAtendimento = useMemo(
    () => OptionsHelper.getOptionsTiposAtendimentoUsuario(currentUser),
    [currentUser]
  )

  const { error: dateError, limits: dateLimits } = useMemo(
    () => validateDateRange(filters.inicio, filters.fim),
    [filters.inicio, filters.fim]
  )

  const debouncedFilters = useDebounce(filters, 500)

  // Período incompleto ou inválido fica fora da busca; os demais filtros continuam valendo.
  const { search, filtersNPS } = useMemo(() => {
    const f = debouncedFilters
    const { error } = validateDateRange(f.inicio, f.fim)
    const periodo = !error && f.range && f.inicio && f.fim

    const search = [
      f.vinculo ? `responsavel.vinculo:${f.vinculo.value}` : '',
      f.empresa ? `paciente.empresa:${f.empresa.value}` : '',
      f.unidade ? `unidade.id:${f.unidade.value}` : '',
      f.tipoAtendimento ? `tipo:${f.tipoAtendimento.value}` : '',
      f.estado ? `estado:${f.estado.value}` : '',
      f.especialidade ? `especialidade:${f.especialidade.value}` : '',
      f.protocolo.trim() ? `protocolo~*${f.protocolo.trim()}*` : '',
      f.paciente.trim() ? `paciente.pessoa.nome~*${f.paciente.trim()}*` : '',
      f.profissional ? `responsavel.pessoa.id:${f.profissional.value}` : '',
      f.modalidade ? `modalidade:${f.modalidade.value}` : '',
      periodo ? `${f.range!.value}>${f.inicio},${f.range!.value}<${f.fim}` : '',
    ]
      .filter(Boolean)
      .join(',')

    // Texto legível dos filtros, impresso no cabeçalho do PDF/CSV pelo backend.
    const filtersNPS = [
      f.empresa ? `Instituição: ${f.empresa.label}` : '',
      f.unidade ? `Unidade: ${f.unidade.label}` : '',
      f.tipoAtendimento ? `Tipo: ${f.tipoAtendimento.label}` : '',
      f.estado ? `Estado: ${f.estado.label}` : '',
      f.especialidade ? `Especialidade: ${f.especialidade.label}` : '',
      f.protocolo.trim() ? `Protocolo: ${f.protocolo.trim()}` : '',
      f.paciente.trim() ? `Paciente: ${f.paciente.trim()}` : '',
      f.profissional ? `Profissional: ${f.profissional.label}` : '',
      f.modalidade ? `Modalidade: ${f.modalidade.label}` : '',
      periodo
        ? `${formatDateToPtBr(f.inicio)}<${RANGE_PDF[String(f.range!.value)]}<${formatDateToPtBr(
            f.fim
          )}`
        : '',
    ]
      .filter(Boolean)
      .join(',')

    return { search, filtersNPS }
  }, [debouncedFilters])

  useEffect(() => {
    updateState({ search, filtersNPS })
  }, [search, filtersNPS, updateState])

  // Unidade depende da instituição: sem instituição, não há unidade.
  useEffect(() => {
    if (!filters.empresa && filters.unidade) setFilter('unidade', undefined)
  }, [filters.empresa, filters.unidade, setFilter])

  const empresaParams = useMemo(() => ({ onlyActive: false }), [])
  const unidadeParams = useMemo(
    () => ({ empresaId: Number(filters.empresa?.value) }),
    [filters.empresa?.value]
  )

  // Período começado e incompleto: destaca o que falta.
  const periodoIniciado = !!(filters.range || filters.inicio || filters.fim)
  const periodoCompleto = !!(filters.range && filters.inicio && filters.fim)
  const faltando = (campo: unknown) => periodoIniciado && !periodoCompleto && !campo
  const erroInicio = faltando(filters.inicio)
    ? 'Selecione uma data inicial'
    : dateError.includes('inicial')
    ? dateError
    : undefined
  const erroFim = faltando(filters.fim)
    ? 'Selecione uma data final'
    : dateError && !dateError.includes('inicial')
    ? dateError
    : undefined

  const limparPeriodo = () => {
    setFilter('range', undefined)
    setFilter('inicio', '')
    setFilter('fim', '')
  }

  const optionChip = (key: SelectFilterKey, label: string): ActiveFilter | undefined => {
    const option = filters[key]
    return option
      ? { key, label, value: option.label, onRemove: () => setFilter(key, undefined) }
      : undefined
  }

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo (o período sai inteiro).
  const activeFilters: ActiveFilter[] = [
    optionChip('empresa', t('empresa.label')),
    periodoCompleto && {
      key: 'periodo',
      label: filters.range!.label,
      value: `${formatDateToPtBr(filters.inicio)} a ${formatDateToPtBr(filters.fim)}`,
      onRemove: limparPeriodo,
    },
    optionChip('unidade', 'Unidade/Setor'),
    filters.paciente.trim() && {
      key: 'paciente',
      label: 'Paciente',
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: 'Protocolo',
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    optionChip('especialidade', 'Especialidade'),
    optionChip('vinculo', 'Vínculo'),
    optionChip('estado', 'Estado'),
    optionChip('profissional', 'Profissional'),
    optionChip('tipoAtendimento', 'Tipo'),
    optionChip('modalidade', 'Modalidade'),
  ].filter((filtro): filtro is ActiveFilter => Boolean(filtro))

  const moreFiltersActiveCount = [
    filters.unidade,
    filters.paciente.trim(),
    filters.protocolo.trim(),
    filters.especialidade,
    filters.vinculo,
    filters.estado,
    filters.profissional,
    filters.tipoAtendimento,
    filters.modalidade,
  ].filter(Boolean).length

  return (
    <TableFilters
      layout='stacked'
      actions={actions}
      moreFiltersActiveCount={moreFiltersActiveCount}
      moreFilters={
        <>
          <TableFilter label='Unidade/Setor'>
            <CustomSelect
              placeholder={
                filters.empresa
                  ? 'Selecione uma unidade'
                  : `Selecione a ${t('empresa.label.lowercase')}`
              }
              onChange={(unidade?: IOption<number>) => setFilter('unidade', unidade ?? undefined)}
              fetchOptions={OptionsHelper.getOptionsUnidades}
              fetchOptionsParams={unidadeParams}
              value={filters.unidade}
              isDisabled={!filters.empresa}
            />
          </TableFilter>
          <TableFilter label='Paciente'>
            <Input
              placeholder='Digite o nome do paciente'
              value={filters.paciente}
              onChange={(e) => setFilter('paciente', e.target.value)}
            />
          </TableFilter>
          <TableFilter label='Protocolo'>
            <Input
              placeholder='Digite o protocolo'
              value={filters.protocolo}
              onChange={(e) => setFilter('protocolo', e.target.value)}
            />
          </TableFilter>
          <TableFilter label='Especialidade'>
            <CustomSelect
              placeholder='Selecione uma especialidade'
              onChange={(especialidade?: IOption<number>) =>
                setFilter('especialidade', especialidade ?? undefined)
              }
              fetchOptions={OptionsHelper.getOptionsEspecialidades}
              value={filters.especialidade}
            />
          </TableFilter>
          <TableFilter label='Vínculo'>
            <CustomSelect
              placeholder='Selecione um vínculo'
              onChange={(vinculo?: IOption<VinculoType>) =>
                setFilter('vinculo', vinculo ?? undefined)
              }
              fetchOptions={OptionsHelper.getOptionsTiposVinculos}
              value={filters.vinculo}
            />
          </TableFilter>
          <TableFilter label='Estado'>
            <CustomSelect
              placeholder='Selecione um estado'
              onChange={(estado?: IOption<number>) => setFilter('estado', estado ?? undefined)}
              fetchOptions={OptionsHelper.getOptionsEstadosAtendimento}
              value={filters.estado}
            />
          </TableFilter>
          <TableFilter label='Profissional de saúde'>
            <CustomSelect
              placeholder='Selecione um profissional'
              onChange={(profissional?: IOption<number>) =>
                setFilter('profissional', profissional ?? undefined)
              }
              fetchOptions={OptionsHelper.getOptionsProfissionalSaudeAdmin}
              value={filters.profissional}
            />
          </TableFilter>
          <TableFilter label='Tipo de atendimento'>
            <CustomSelect
              placeholder='Selecione um tipo'
              options={optionsTiposAtendimento}
              onChange={(tipo?: IOption<AtendimentoType>) =>
                setFilter('tipoAtendimento', tipo ?? undefined)
              }
              value={filters.tipoAtendimento}
            />
          </TableFilter>
          <TableFilter label='Modalidade'>
            <CustomSelect
              placeholder='Selecione uma modalidade'
              onChange={(modalidade?: IOption<ModalidadeType>) =>
                setFilter('modalidade', modalidade ?? undefined)
              }
              fetchOptions={OptionsHelper.getOptionsModalidadesAtendimento}
              value={filters.modalidade}
            />
          </TableFilter>
        </>
      }
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={isLoading ? 'Buscando…' : undefined}
        />
      }
    >
      <TableFilter label={t('empresa.label')} wide>
        <CustomSelect
          entity='empresa'
          onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEmpresasAdmin}
          fetchOptionsParams={empresaParams}
          value={filters.empresa}
        />
      </TableFilter>
      <TableFilter
        label='Consultas entre'
        error={faltando(filters.range) ? 'Selecione uma opção' : undefined}
      >
        <CustomSelect
          placeholder='Selecione uma opção'
          options={OptionsHelper.consultaDatasOptions}
          onChange={(range?: IOption<string>) => setFilter('range', range ?? undefined)}
          value={filters.range}
        />
      </TableFilter>
      <TableFilter label='Data inicial' error={erroInicio}>
        <DateInput
          value={filters.inicio}
          onChange={(e) => setFilter('inicio', e.target.value)}
          minDate={dateLimits.startMin}
          maxDate={dateLimits.startMax}
          isInvalid={Boolean(erroInicio)}
        />
      </TableFilter>
      <TableFilter label='Data final' error={erroFim}>
        <DateInput
          value={filters.fim}
          onChange={(e) => setFilter('fim', e.target.value)}
          minDate={dateLimits.endMin}
          maxDate={dateLimits.endMax}
          isInvalid={Boolean(erroFim)}
        />
      </TableFilter>
    </TableFilters>
  )
}
